import { Send } from 'lucide-react'
import type { Invitee } from '../types/invitee'
import { formatPhoneNumber, formatCurrency, formatDateTimeForDisplay } from '../utils/inviteeFormatters'
import { ALLOW_PROGRESS_STATUSES, shouldShowInviteeGridActions } from '../utils/inviteeConstants'
import styles from '../styles/invitesParity.module.css'

interface InviteeGridProps {
  invitees: Invitee[]
  onResend?: (inviteCode: string) => void
  onEdit?: (inviteCode: string) => void
  onAddCoApplicant?: (invite: Invitee) => void
  showActions?: boolean
}

export function InviteeGrid({ invitees, onResend, onEdit, onAddCoApplicant, showActions = true }: InviteeGridProps) {
  const canShowActions = (invite: Invitee) => {
    return (
      invite.invite_accepted !== '1' &&
      invite.invite_accepted !== 1 &&
      invite.show_action_button === 1
    )
  }

  const canShowAddCoApplicant = (invite: Invitee) => {
    // Max 4 co-applicants allowed
    const coApplicantCount = invite.co_applicant?.length || 0;
    if (coApplicantCount >= 4) return false;
    return (
      canShowActions(invite) ||
      (invite.status && ALLOW_PROGRESS_STATUSES.includes(invite.status as any))
    )
  }

  return (
    <div
      data-testid="invitee-list-grid"
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
    >
      {invitees.map((invite) => {
        const canAct = canShowActions(invite)
        const showAddCoApplicant = canShowAddCoApplicant(invite)
        const showActionsInGrid = shouldShowInviteeGridActions(invite.status)
        const cardSuffix = String(invite.invite_code).replace(/[^a-zA-Z0-9_-]/g, '_')

        return (
          <div
            key={invite.invite_code}
            data-testid={`invitee-list-grid-card-${cardSuffix}`}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <div className="space-y-1.5">
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-property-staff`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Property Staff</p>
                <p className="text-sm font-medium text-gray-900">{(invite.agent_name ?? (invite as any).property_staff_name) || '-'}</p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-applicant-name`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Applicant Name</p>
                <p className="text-sm font-medium text-gray-900">{invite.applicant_name}</p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-email`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Email</p>
                <p className="text-sm text-gray-900">{invite.email}</p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-phone`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Phone</p>
                <p className="text-sm text-gray-900">{formatPhoneNumber(invite.mobile_no)}</p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-rent`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Rent/ Mo ($)</p>
                <p className="text-sm text-gray-900">{formatCurrency(invite.rent_amount)}</p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-unit`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Unit</p>
                <p className="text-sm text-gray-900">{invite.unit_number || '-'}</p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-status`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Status</p>
                <span className="inline-block px-2 py-1 text-xs rounded-full bg-[rgb(21_151_255)]/12 text-[rgb(21_151_255)]">
                  {invite.status}
                </span>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-created`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Created</p>
                <p className="text-sm text-gray-900">{formatDateTimeForDisplay(invite.created_at)}</p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-status-updated`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Status Updated</p>
                <p className="text-sm text-gray-900">
                  {invite.invite_status_date ? formatDateTimeForDisplay(invite.invite_status_date) : '-'}
                </p>
              </div>
              <div data-testid={`invitee-list-grid-card-${cardSuffix}-invite-code`}>
                <p className="text-xs font-medium leading-tight text-gray-500">Invite Code</p>
                <p className="text-sm font-mono text-gray-900">{invite.invite_code}</p>
              </div>
              {showActions && showActionsInGrid && (
                <div data-testid={`invitee-list-grid-card-${cardSuffix}-actions`}>
                  <p className="text-xs font-medium leading-tight text-gray-500">Actions</p>
                  <div className={`${styles.actionButtons} flex-wrap`}>
                    {canAct && onResend && (
                      <button
                        type="button"
                        data-testid={`invitee-list-grid-resend-button-${cardSuffix}`}
                        onClick={() => onResend(invite.invite_code)}
                        className={styles.iconAction}
                        title="Re-Send Invite"
                      >
                        <Send className="h-4 w-4" />
                      </button>
                    )}
                    {canAct && onEdit && (
                      <button
                        type="button"
                        data-testid={`invitee-list-grid-edit-button-${cardSuffix}`}
                        onClick={() => onEdit(invite.invite_code)}
                        className={styles.textAction}
                        title="Edit"
                      >
                        Edit
                      </button>
                    )}
                    {showAddCoApplicant && onAddCoApplicant && (
                      <button
                        type="button"
                        data-testid={`invitee-list-grid-add-co-applicant-button-${cardSuffix}`}
                        onClick={() => onAddCoApplicant(invite)}
                        className={styles.textAction}
                        title="Add Co-Applicant"
                      >
                        Add Co-Applicant
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
